import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import PhotoSizeSelectLargeIcon from "@mui/icons-material/PhotoSizeSelectLarge";
import StayPrimaryPortraitIcon from "@mui/icons-material/StayPrimaryPortrait";
import StayPrimaryLandscapeIcon from "@mui/icons-material/StayPrimaryLandscape";
import TuneIcon from "@mui/icons-material/Tune";
import EditIcon from "../../assets/svg/EditIcon";
import Button from "../Buttons/Button";
import Loader from "../Loaders/Loader";
import ModalResizeCustomImage from "./ModalResizeCustomImage";
import { useTranslationApp } from "../../lib/i18next";
import { useMediaDimensions } from "../../hooks/useMediaDimensions";
import { getDimensionCategory } from "../../utils/dimensions";
import { MEDIA_DIMENSIONS } from "../../utils/constants";
import {
  BUTTON_RESIZE_IMAGE,
  EDIT_WITH_CANVA,
  MULTI_FORMAT_RESIZE_FORMATS_COUNT,
  MULTI_FORMAT_RESIZE_ONE_FORMAT,
  RESIZE_CUSTOM,
  RESIZE_MENU_LABEL,
  RESIZE_TO_FEED,
  RESIZE_TO_HORIZONTAL,
  RESIZE_TO_SQUARE,
  RESIZE_TO_VERTICAL,
} from "../../i18n/keysTranslations";

const ALL_FORMATS = [
  MEDIA_DIMENSIONS.square,
  MEDIA_DIMENSIONS.feed,
  MEDIA_DIMENSIONS.vertical,
  MEDIA_DIMENSIONS.horizontal,
  MEDIA_DIMENSIONS.custom,
];

const formatIcons = {
  square: CropSquareIcon,
  vertical: StayPrimaryPortraitIcon,
  horizontal: StayPrimaryLandscapeIcon,
  feed: StayPrimaryPortraitIcon,
  custom: TuneIcon,
};

const formatTranslationKeys = {
  square: RESIZE_TO_SQUARE,
  vertical: RESIZE_TO_VERTICAL,
  horizontal: RESIZE_TO_HORIZONTAL,
  feed: RESIZE_TO_FEED,
  custom: RESIZE_CUSTOM,
};

const menuAnchorProps = {
  anchorOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
};

const MultiFormatResizeMenu = ({
  id = "",
  url,
  variant = "full",
  onResizeFormat = () => {},
  additionalMenuItems = [],
  hideResizeOptions = false,
  disabled = false,
  loading = false,
  sx = {},
  iconColor = "white",
  mode = "icon",
  textButton = "",
  colorButton = "primary",
  showAllFormats = false,
  TriggerIcon = null,
}) => {
  const { t } = useTranslationApp();
  const { aspectRatio, loading: dimensionsLoading } = useMediaDimensions(
    showAllFormats ? null : url
  );
  const category = getDimensionCategory(aspectRatio);

  const missingFormats = useMemo(() => {
    if (showAllFormats) return ALL_FORMATS;
    if (dimensionsLoading) return [];
    return ALL_FORMATS.filter((format) => format !== category);
  }, [category, dimensionsLoading, showAllFormats]);

  const [mainAnchorEl, setMainAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [customDimensions, setCustomDimensions] = useState(null);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const triggerAnchorRef = useRef(null);
  const resizeMenuItemRef = useRef(null);
  const mainAnchorBeforeModalRef = useRef(null);
  const submenuAnchorBeforeModalRef = useRef(null);
  const pendingSubmenuRestoreRef = useRef(false);
  const isSubmittingResizeRef = useRef(false);
  const [isSubmittingResize, setIsSubmittingResize] = useState(false);

  const isMainOpen =
    Boolean(mainAnchorEl) && !isCustomModalOpen && !isSubmittingResize;
  const isSubmenuOpen =
    Boolean(submenuAnchorEl) && !isCustomModalOpen && !isSubmittingResize;

  const lockAndCloseMenus = useCallback(() => {
    isSubmittingResizeRef.current = true;
    pendingSubmenuRestoreRef.current = false;
    mainAnchorBeforeModalRef.current = null;
    submenuAnchorBeforeModalRef.current = null;
    setIsSubmittingResize(true);
    setMainAnchorEl(null);
    setSubmenuAnchorEl(null);
    setSelectedFormats([]);
    setCustomDimensions(null);
  }, []);

  const resetResizeSubmissionState = useCallback(() => {
    isSubmittingResizeRef.current = false;
    setIsSubmittingResize(false);
  }, []);

  const onOpenMainMenu = (event) => {
    event?.stopPropagation?.();
    resetResizeSubmissionState();
    triggerAnchorRef.current = event.currentTarget;

    if (variant === "resizeOnly") {
      setSubmenuAnchorEl(event.currentTarget);
      return;
    }

    setMainAnchorEl(event.currentTarget);
  };

  const onCloseMenus = () => {
    setMainAnchorEl(null);
    setSubmenuAnchorEl(null);
  };

  const onOpenSubmenu = (event) => {
    event?.stopPropagation?.();
    setSubmenuAnchorEl(event.currentTarget);
  };

  const onCloseSubmenuOnly = () => {
    setSubmenuAnchorEl(null);
  };

  const onOpenCustomModal = () => {
    mainAnchorBeforeModalRef.current = mainAnchorEl;

    if (variant === "resizeOnly") {
      submenuAnchorBeforeModalRef.current = submenuAnchorEl;
    } else {
      pendingSubmenuRestoreRef.current = Boolean(submenuAnchorEl);
    }

    setMainAnchorEl(null);
    setSubmenuAnchorEl(null);
    setIsCustomModalOpen(true);
  };

  const onResizeMenuItemRef = useCallback((node) => {
    resizeMenuItemRef.current = node;
  }, []);

  const onMainMenuEntered = useCallback(() => {
    if (isSubmittingResizeRef.current || !pendingSubmenuRestoreRef.current) {
      return;
    }

    pendingSubmenuRestoreRef.current = false;
    if (resizeMenuItemRef.current) {
      setSubmenuAnchorEl(resizeMenuItemRef.current);
    }
  }, []);

  const onRestoreMenusAfterCustomModal = useCallback(() => {
    if (mainAnchorBeforeModalRef.current) {
      setMainAnchorEl(mainAnchorBeforeModalRef.current);
    }
    mainAnchorBeforeModalRef.current = null;

    if (variant === "resizeOnly") {
      if (submenuAnchorBeforeModalRef.current) {
        setSubmenuAnchorEl(submenuAnchorBeforeModalRef.current);
      }
      submenuAnchorBeforeModalRef.current = null;
    }
    // For variant "full", the submenu anchor is a MenuItem that unmounts
    // whenever the parent menu closes, so its DOM node becomes stale, and
    // the parent menu's own Grow transition means its final position isn't
    // settled right when it mounts. We defer restoring the submenu until
    // the parent menu's open transition finishes (see onMainMenuEntered),
    // so the submenu anchors to the item's final, stable position.
  }, [variant]);

  const onToggleFormat = (format) => {
    setSelectedFormats((prev) => {
      if (prev.includes(format)) {
        if (format === MEDIA_DIMENSIONS.custom) {
          setCustomDimensions(null);
        }
        return prev.filter((item) => item !== format);
      }
      if (format === MEDIA_DIMENSIONS.custom) {
        onOpenCustomModal();
        return prev;
      }
      return [...prev, format];
    });
  };

  const onConfirmCustomDimensions = useCallback(
    ({ width, height }) => {
      setCustomDimensions({ width, height });
      setSelectedFormats((prev) => {
        if (prev.includes(MEDIA_DIMENSIONS.custom)) {
          return prev;
        }
        return [...prev, MEDIA_DIMENSIONS.custom];
      });
      setIsCustomModalOpen(false);
      onRestoreMenusAfterCustomModal();
    },
    [onRestoreMenusAfterCustomModal]
  );

  const onCloseCustomModal = useCallback(() => {
    setIsCustomModalOpen(false);
    onRestoreMenusAfterCustomModal();
  }, [onRestoreMenusAfterCustomModal]);

  const onSubmitResize = async (event) => {
    event?.stopPropagation?.();
    if (selectedFormats.length === 0) return;

    const includesCustom = selectedFormats.includes(MEDIA_DIMENSIONS.custom);
    if (includesCustom && !customDimensions) return;

    try {
      const result = await onResizeFormat({
        formats: selectedFormats,
        ...(includesCustom && customDimensions ? customDimensions : {}),
        onCreditsConfirmed: lockAndCloseMenus,
      });

      if (result?.cancelled) {
        resetResizeSubmissionState();
      }
    } catch (error) {
      resetResizeSubmissionState();
      throw error;
    } finally {
      resetResizeSubmissionState();
    }
  };

  const selectedCount = selectedFormats.length;
  const includesCustom = selectedFormats.includes(MEDIA_DIMENSIONS.custom);
  const showResizeEntry = !hideResizeOptions && missingFormats.length > 0;
  const isSubmitDisabled =
    selectedCount === 0 || (includesCustom && !customDimensions);

  const formatsCountLabel =
    selectedCount === 1
      ? t(MULTI_FORMAT_RESIZE_ONE_FORMAT)
      : t(MULTI_FORMAT_RESIZE_FORMATS_COUNT, { count: selectedCount });

  useEffect(() => {
    if (!isSubmenuOpen || isCustomModalOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      event.stopPropagation();

      if (variant === "resizeOnly") {
        onCloseMenus();
      } else {
        onCloseSubmenuOnly();
      }

      triggerAnchorRef.current?.focus?.();
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isCustomModalOpen, isSubmenuOpen, variant]);

  if (variant === "resizeOnly" && !showAllFormats) {
    if (!url || dimensionsLoading || missingFormats.length === 0) {
      return null;
    }
  }

  if (mode === "icon" && loading) {
    return <Loader colorCustom="white" hasMarginTop={false} size={32} />;
  }

  const triggerLabel = t(
    variant === "full" ? EDIT_WITH_CANVA : RESIZE_MENU_LABEL
  );

  const trigger =
    mode === "footer" ? (
      <Button
        id={id}
        color="secondary"
        iconOnly
        ariaLabel={triggerLabel}
        tooltip={triggerLabel}
        disabled={disabled}
        startIcon={
          TriggerIcon ? (
            <TriggerIcon sx={{ fontSize: 24 }} />
          ) : (
            <EditIcon width="24px" height="24px" />
          )
        }
        onClick={onOpenMainMenu}
      />
    ) : mode === "icon" ? (
      <IconButton
        onClick={onOpenMainMenu}
        sx={sx}
        edge={false}
        disabled={disabled}
        aria-label={triggerLabel}
      >
        {TriggerIcon ? (
          <TriggerIcon sx={{ fontSize: 24, color: iconColor }} />
        ) : (
          <EditIcon color={iconColor} width="24px" height="24px" />
        )}
      </IconButton>
    ) : (
      <Button
        loading={loading}
        disabled={disabled}
        color={colorButton}
        onClick={onOpenMainMenu}
        sx={sx}
      >
        {t(textButton)}
      </Button>
    );

  return (
    <>
      {trigger}

      {variant === "full" ? (
        <Menu
          anchorEl={mainAnchorEl}
          open={isMainOpen}
          onClose={onCloseMenus}
          TransitionProps={{ onEntered: onMainMenuEntered }}
          {...menuAnchorProps}
          PaperProps={{
            sx: isSubmittingResize ? { pointerEvents: "none" } : undefined,
          }}
          MenuListProps={{
            onClick: (event) => event?.stopPropagation?.(),
            onMouseDown: (event) => event?.stopPropagation?.(),
          }}
        >
          {showResizeEntry ? (
            <MenuItem
              ref={onResizeMenuItemRef}
              onClick={onOpenSubmenu}
              aria-haspopup="menu"
              aria-expanded={isSubmenuOpen}
            >
              <PhotoSizeSelectLargeIcon sx={{ fontSize: 16, mr: 1 }} />
              <ListItemText primary={t(RESIZE_MENU_LABEL)} />
              <ChevronRightIcon fontSize="small" />
            </MenuItem>
          ) : null}

          {additionalMenuItems.map((item) => (
            <MenuItem
              key={item.label}
              disabled={isSubmittingResize}
              onClick={(event) => {
                event?.stopPropagation?.();
                if (isSubmittingResizeRef.current) return;
                lockAndCloseMenus();
                item.onClick?.();
              }}
            >
              {item.icon}
              {t(item.label)}
            </MenuItem>
          ))}
        </Menu>
      ) : null}

      <Menu
        anchorEl={submenuAnchorEl}
        open={isSubmenuOpen}
        onClose={variant === "resizeOnly" ? onCloseMenus : onCloseSubmenuOnly}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          role: "menu",
          onClick: (event) => event?.stopPropagation?.(),
          onMouseDown: (event) => event?.stopPropagation?.(),
        }}
        PaperProps={{ sx: { minWidth: 260 } }}
      >
        {missingFormats.map((format) => {
          const Icon = formatIcons[format];
          const isChecked = selectedFormats.includes(format);
          const isCustomFormat = format === MEDIA_DIMENSIONS.custom;
          const customDimensionsLabel =
            isCustomFormat && customDimensions
              ? `${customDimensions.width}x${customDimensions.height}`
              : null;

          return (
            <MenuItem
              key={format}
              role="menuitemcheckbox"
              aria-checked={isChecked}
              onClick={() => onToggleFormat(format)}
              dense
            >
              <Checkbox
                checked={isChecked}
                tabIndex={-1}
                disableRipple
                size="small"
                sx={{ p: 0.5, mr: 1 }}
              />
              <Icon sx={{ fontSize: 16, mr: 1 }} />
              <ListItemText
                primary={t(formatTranslationKeys[format])}
                secondary={customDimensionsLabel}
                secondaryTypographyProps={{
                  variant: "caption",
                  color: "text.secondary",
                }}
              />
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatsCountLabel}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 1 }}
            disabled={isSubmitDisabled}
            onClick={onSubmitResize}
          >
            {t(BUTTON_RESIZE_IMAGE)}
          </Button>
        </Box>
      </Menu>

      <ModalResizeCustomImage
        modalOpen={isCustomModalOpen}
        onCloseModal={onCloseCustomModal}
        onCallbackResize={onConfirmCustomDimensions}
      />
    </>
  );
};

export default MultiFormatResizeMenu;
